'use client';

import { IconAlertCircle } from '@repo/svgs';
import { Button } from '@/components/ui';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { activeApplicationsPhrase } from '@/lib/applications/limit';

/**
 * The active-application limit dialogs.
 *
 * A renter may hold at most `limit` ACTIVE applications (see
 * LIMIT_COUNTING_REVIEW_STATUSES on the backend — drafts, denied, withdrawn
 * and signed & paid don't count). Two shapes, from the same Figma flow:
 *
 * - `reached` — a hard stop. They're at the limit, so starting a new
 *   application (or opening a draft, which would only become one) is refused.
 *   One action, whose label depends on where it's shown: "View applications"
 *   from the unit page, "Okay" from the applications list itself, where
 *   navigating to the list the user is already on would be a no-op.
 *
 * - `reminder` — a soft heads-up. They have a lot of DRAFTS but are still
 *   under the active limit. Drafts are uncapped, so this explains the ceiling
 *   they'll hit at submit time and then lets them carry on.
 *
 * Shares EditApplicationModal's shell so the three dialogs in this flow are
 * visually identical: centered dialog on desktop, bottom sheet on mobile.
 */
type ApplicationLimitDialogProps = {
  open: boolean;
  /** Dismiss without acting (X, overlay, Esc). */
  onClose: () => void;
  /** Max active applications, named in the copy so the two never disagree. */
  limit: number;
} & (
  | {
      variant: 'reached';
      /** How many they currently hold — normally equal to `limit`. */
      activeCount: number;
      /** "View applications" from a unit page, "Okay" from the list. */
      actionLabel: string;
      onAction: () => void;
    }
  | {
      variant: 'reminder';
      /** Named in the question, e.g. "402 E 92nd St #11C". */
      address: string;
      onViewApplications: () => void;
      /** Proceed — drafts aren't capped, so this is allowed. */
      onStart: () => void;
    }
);

export function ApplicationLimitDialog(props: ApplicationLimitDialogProps) {
  const { open, onClose, limit } = props;
  const isReached = props.variant === 'reached';

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      label={isReached ? 'Application limit reached' : 'Application limit reminder'}
      maxWidth="md"
      // Matches EditApplicationModal exactly — see its comments for why these
      // override the shell's stock padding and radius.
      contentPaddingClassName="px-4 pt-5 pb-6"
      contentClassName="rounded-[32px]"
      desktopBodyClassName="rounded-[32px]"
    >
      <div className="flex flex-col gap-4">
        <div className="px-2">
          {/* Red for the hard stop, amber for the advisory — the only visual
              difference between the two shapes. */}
          <IconAlertCircle
            className={`h-8 w-8 ${isReached ? 'icon-alert' : 'icon-caution'}`}
            aria-hidden
          />
        </div>

        <div className="flex flex-col gap-2 px-2 text-primary">
          <p className="text-heading-6">
            {isReached
              ? `You have ${activeApplicationsPhrase(props.activeCount)}`
              : `Start an application for ${props.address}?`}
          </p>
          <p className="text-body-2">
            {isReached
              ? `You can have up to ${activeApplicationsPhrase(limit)} at a time. Once one is processed, you'll be able to apply again.`
              : `You can keep creating drafts, but you can only have up to ${activeApplicationsPhrase(limit)} at a time.`}
          </p>
        </div>

        {/* Single full-width action for the hard stop; two actions for the
            advisory. `flex-col-reverse` keeps DOM order matching desktop's
            left-to-right visual order — same tradeoff as
            EditApplicationModal, documented there. */}
        {isReached ? (
          <div className="px-2">
            <Button variant="primary" size="md" fullWidth onClick={props.onAction}>
              {props.actionLabel}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col-reverse md:flex-row items-start justify-center gap-4 px-2">
            <Button variant="tertiary" size="md" fullWidth onClick={props.onViewApplications}>
              View applications
            </Button>
            <Button variant="primary" size="md" fullWidth onClick={props.onStart}>
              Start application
            </Button>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
